import React, { useState, FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import useQuery from "Hooks/query.hook";
import { DataTable } from "@life-line-lab/components-react";
import { Divider, Spin } from "antd";
import {
  CaretDownOutlined,
  CaretUpOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import ReportFilters from "./report-filters";
import useSearch from "Hooks/search.hook";
import DataTableFilter from "Components/shared/data-table-filter";
import { toCapitalisedSpaced } from "Helpers/to-capitalised-spaced";
import { getStatusStyle } from "Helpers/get-status-colours";

interface TripReportListProp {
  onFilter: (filterInput: any) => void;
  reportListing: any[];
  handleDownloadExcelFile: (
    tableData: any[],
    excelType: string,
    sheetName?: string,
  ) => Promise<void>;
  itemPerPage: number;
  isLoading: boolean;
  handleRefreshList: () => void;
  isExcelLoading: boolean;
  handleListingTripDetails: (tripID: string) => Promise<void>;
  tripDetails: { [key: string]: any[] };
  isLoadingTripDetails: { [key: string]: boolean };
}

const TripReportList: FC<TripReportListProp> = (props) => {
  const {
    onFilter,
    reportListing,
    handleDownloadExcelFile,
    itemPerPage,
    isLoading,
    handleRefreshList,
    isExcelLoading,
    handleListingTripDetails,
    tripDetails,
    isLoadingTripDetails,
  } = props || {};

  const query = useQuery();
  const { t } = useTranslation();
  const page: any = query.get("page") || 1;

  const [filterState, setFilterState] = useState<any>({
    status: "",
    sortByCreatedAt: "DESC",
  });

  const { handleSearch } = useSearch({ onFilter, filterState, setFilterState });

  const columns: any = [
    {
      title: "#",
      dataIndex: "index",
      render: (text: any, record: any, index: number) =>
        (page - 1) * itemPerPage + (index + 1),
    },
    {
      title: t("label.startDateTime").toLocaleUpperCase(),
      dataIndex: "startDateTime",
      render: (startDateTime: string) => {
        return startDateTime
          ? moment(startDateTime).format("DD-MM-YYYY | hh:mm a")
          : "-";
      },
    },
    {
      title: t("label.endDateTime").toLocaleUpperCase(),
      dataIndex: "endDateTime",
      render: (endDateTime: string) => {
        return endDateTime
          ? moment(endDateTime).format("DD-MM-YYYY | hh:mm a")
          : "-";
      },
    },
    {
      title: t("label.origin").toLocaleUpperCase(),
      dataIndex: ["origin", "locationName"],
    },
    {
      title: t("label.destination").toLocaleUpperCase(),
      dataIndex: ["destination", "locationName"],
    },
    {
      title: t("label.vehicle").toLocaleUpperCase(),
      dataIndex: "vehicle",
    },
    {
      title: t("label.vehicleNo").toLocaleUpperCase(),
      dataIndex: "vehiclePlate",
    },
    {
      title: t("label.vehicleType").toLocaleUpperCase(),
      dataIndex: "vehicleType",
    },
    {
      title: t("label.driver").toLocaleUpperCase(),
      dataIndex: "driver",
    },
    {
      title: t("label.stop").toLocaleUpperCase(),
      dataIndex: "stops",
    },
    {
      title: t("label.noOfParcel").toLocaleUpperCase(),
      dataIndex: "parcels",
      render: (parcels: any[]) => {
        return parcels.length;
      },
    },
    {
      title: t("label.cubicVolume").toLocaleUpperCase(),
      dataIndex: "volume_cm3",
    },
    {
      title: t("label.volLoadFactor").toLocaleUpperCase(),
      dataIndex: "volumeLoadfactor_percentage",
    },
    {
      title: t("label.weightInKg").toLocaleUpperCase(),
      dataIndex: "weight_kg",
    },
    {
      title: t("label.weightLoadFactor").toLocaleUpperCase(),
      dataIndex: "weightLoadfactor_percentage",
    },
    {
      title: t("label.distanceInKm").toLocaleUpperCase(),
      dataIndex: "distance_km",
    },
    {
      title: t("label.estimated_GHG_gCO2e").toLocaleUpperCase(),
      dataIndex: "estimated_GHG_gCO2e",
    },
    {
      title: t("label.status").toLocaleUpperCase(),
      dataIndex: "status",
      render: (text: any) => {
        return (
          <span className="status-indicator" style={getStatusStyle(text)}>
            {text ? toCapitalisedSpaced(text) : "-"}
          </span>
        );
      },
    },
  ];

  const sortByCreatedAt = useMemo(() => {
    return [
      { label: t("option.latest"), value: "DESC" },
      { label: t("option.earliest"), value: "ASC" },
    ];
  }, []);

  const onExpandRow = async (expanded: boolean, tripID: string) => {
    if (expanded && !tripDetails[tripID]) {
      await handleListingTripDetails(tripID);
    }
  };

  const expandable = {
    expandIcon: ({ expanded, onExpand, record }) =>
      expanded ? (
        <CaretUpOutlined
          onClick={(e) =>
            onExpandRow(false, record._id).then(() => onExpand(record, e))
          }
        />
      ) : (
        <CaretDownOutlined
          onClick={(e) =>
            onExpandRow(true, record._id).then(() => onExpand(record, e))
          }
        />
      ),
    expandedRowRender: (record: any) => {
      return (
        <>
          {isLoadingTripDetails[record._id] ? (
            <div className="loading-container">
              <Spin size="large" indicator={<LoadingOutlined />} />
            </div>
          ) : (
            <DataTable
              rowKey="_id"
              width={50}
              columns={columns}
              dataSource={tripDetails[record._id] || []}
            />
          )}
        </>
      );
    },
  };

  const handleFilterChange = (value: any, name: string) => {
    let selectedValue;

    switch (name) {
      case "dateFrom":
        selectedValue = value ? value.startOf("day").toISOString() : null;
        break;
      case "dateTo":
        selectedValue = value ? value.endOf("day").toISOString() : null;
        break;
      case "onlyLatestStatus":
        selectedValue = value ? "true" : "false"; // boolean will be cleared by the hook
        break;
      default:
        selectedValue = value;
    }

    if (Array.isArray(value)) {
      selectedValue = value.filter((item) => item !== "all");
    }
    setFilterState({ ...filterState, [name]: selectedValue });
  };

  return (
    <div>
      <div className="dm-report-sort-container">
        <ReportFilters
          handleFilterChange={handleFilterChange}
          filterState={filterState}
          handleDownloadExcelFile={handleDownloadExcelFile}
          excelType={"trip"}
          reportListing={reportListing}
          statusOptions={[]}
          sortByCreatedAt={sortByCreatedAt}
          handleRefreshList={handleRefreshList}
          isLoading={isLoading}
          isExcelLoading={isExcelLoading}
        />
        <Divider />
        <DataTableFilter
          searchPlaceholder={t("label.searchReportingTrip")}
          onSearch={(text: string) => handleSearch(text)}
        />
      </div>
      {isLoading ? (
        <div className="loading-container">
          <Spin size="large" indicator={<LoadingOutlined />} />
        </div>
      ) : (
        <DataTable
          rowKey="_id"
          expandable={expandable}
          columns={columns}
          scroll={{ x: "max-content" }}
          dataSource={reportListing}
        />
      )}
    </div>
  );
};

export default TripReportList;
